import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import CTABand from "../components/CTABand";
import PageMeta from "../components/PageMeta";

const IMG = (id) => `https://images.unsplash.com/photo-${id}?q=80&w=1600&auto=format&fit=crop`;

const SOLUTIONS = [
  {
    id: "hospital-setup", title: "Hospital Setup & Turnkey Solutions", img: IMG("1764885517847-79d62138cc58"),
    blurb: "Comprehensive support for establishing and upgrading healthcare facilities, covering equipment, furniture, infrastructure requirements and project coordination.",
    points: ["Facility requirement assessment", "Department-wise equipment planning", "Infrastructure requirement coordination", "Procurement and installation support", "Single-window project coordination"],
  },
  {
    id: "equipment", title: "Medical Equipment & Devices", img: IMG("1655635643486-a17bc48771ff"),
    blurb: "A comprehensive portfolio of medical equipment, surgical products, diagnostic equipment, hospital furniture and healthcare consumables.",
    points: ["Multi-brand sourcing coordination", "Delivery, installation and orientation", "Consumables replenishment planning", "Warranty and service coordination"],
  },
  {
    id: "dialysis", title: "Dialysis Centre Solutions", img: IMG("1778151270757-f3793f65e2de"),
    blurb: "Support for dialysis centre planning, equipment, furniture and operational requirements.",
    points: ["Dialysis chairs", "Dialysis equipment", "Patient monitoring", "RO / water treatment requirements", "Supporting infrastructure", "Installation and service"],
  },
  {
    id: "ot-critical-care", title: "OT & Critical Care", img: IMG("1775947933085-30050ddad6b3"),
    blurb: "Operating theatre and intensive care equipment for hospitals, nursing homes and surgical centres.",
    points: ["OT tables", "OT equipment", "Patient monitors", "Suction systems", "Anaesthesia-related equipment", "Surgical instruments", "ICU equipment", "Critical-care furniture"],
  },
  {
    id: "diagnostic", title: "Diagnostic & Laboratory Solutions", img: IMG("1602052577122-f73b9710adba"),
    blurb: "Laboratory and diagnostic setups for hospitals, diagnostic centres and clinics.",
    points: ["Laboratory equipment", "Diagnostic equipment", "Patient monitoring", "Supporting consumables", "Facility requirements"],
  },
  {
    id: "furniture", title: "Hospital Furniture", img: IMG("1778151270902-cb0ca572f2ee"),
    blurb: "Furniture programmes for wards, ICUs, OTs and patient areas.",
    points: ["Hospital beds", "Fowler beds", "ICU beds", "Examination couches", "OT tables", "Dialysis chairs", "Patient furniture", "Hospital storage"],
  },
];

const Solutions = () => (
  <main data-testid="solutions-page">
    <PageMeta title="Healthcare Solutions | AccuSurge Medical Solutions" description="Hospital setup and turnkey solutions, medical equipment, dialysis centre solutions, OT & critical care, diagnostic & laboratory solutions and hospital furniture — coordinated by one partner." />
    <section className="bg-brand-950 pb-20 pt-40 md:pb-28 grain relative overflow-hidden">
      <div className="absolute -left-20 bottom-0 h-80 w-80 rounded-full bg-brand-600/30 blur-[110px]" aria-hidden="true" />
      <div className="container-x relative">
        <Reveal>
          <span className="eyebrow text-accent">Healthcare Solutions</span>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl md:text-6xl font-semibold tracking-tighter text-white text-balance">
            From Medical Equipment to Complete Healthcare Solutions.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-white/70">
            AccuSurge Medical Solutions partners with healthcare providers to simplify procurement, project execution
            and ongoing support — helping turn healthcare plans into operational facilities.
          </p>
        </Reveal>
      </div>
    </section>

    <nav aria-label="Solutions" className="sticky top-[76px] z-30 border-b border-slate-200 bg-white/85 backdrop-blur-xl" data-testid="solutions-nav">
      <div className="container-x flex gap-2 overflow-x-auto py-3 [scrollbar-width:none]">
        {SOLUTIONS.map((s) => (
          <a key={s.id} href={`#${s.id}`} className="chip shrink-0 !px-4 !py-2 transition-colors hover:!border-brand-400 hover:!text-brand-700">
            {s.title}
          </a>
        ))}
      </div>
    </nav>

    {SOLUTIONS.map((s, i) => (
      <section key={s.id} id={s.id} className={`scroll-mt-32 py-20 md:py-28 ${i % 2 ? "bg-slate-100" : "bg-white"}`} data-testid={`solution-section-${s.id}`}>
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <Reveal className={i % 2 ? "lg:order-2" : ""}>
            <div className="overflow-hidden rounded-2xl">
              <img src={s.img} alt={s.title} loading="lazy" className="h-[320px] w-full object-cover md:h-[420px]" />
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <span className="font-mono text-xs text-accent-600">{String(i + 1).padStart(2, "0")}</span>
            <h2 className="mt-3 font-heading text-3xl md:text-4xl font-semibold tracking-tight text-ink">{s.title}</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">{s.blurb}</p>
            <ul className="mt-7 grid gap-3 sm:grid-cols-2">
              {s.points.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent-600" /> {p}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/products" className="btn-teal" data-testid={`solution-cta-products-${s.id}`}>
                Explore Related Products <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="btn-outline">Talk to Our Expert</Link>
            </div>
          </Reveal>
        </div>
      </section>
    ))}

    <CTABand />
  </main>
);

export default Solutions;
